import { requireNumber, type CalculatorIssues } from "./calculator-input";
import { AWG_WIRE_TABLE, voltageDrop, WIRE_TABLE } from "./wire-tables";

/** Keep exact conductor areas independent of the chosen length display units. */
export const VOLTAGE_DROP_WIRES = [
  ...WIRE_TABLE.map(wire => ({ mm2: wire.mm2, label: `${wire.mm2} mm²`, group: "Metric" })),
  ...AWG_WIRE_TABLE.map(wire => ({ mm2: wire.mm2, label: `${wire.awg} AWG`, group: "AWG" })),
];

export type VoltageDropInput = {
  voltageV: number | null;
  currentA: number | null;
  oneWayLengthM: number | null;
  wireMm2: number | null;
  targetPct: 3 | 10 | null;
};

export const EMPTY_VOLTAGE_DROP_INPUT: VoltageDropInput = {
  voltageV: null, currentA: null, oneWayLengthM: null, wireMm2: null, targetPct: null,
};
export const EXAMPLE_VOLTAGE_DROP_INPUT: VoltageDropInput = {
  voltageV: 12, currentA: 20, oneWayLengthM: 5, wireMm2: 6, targetPct: 3,
};

export type VoltageDropResult = CalculatorIssues & {
  status: "unknown" | "invalid" | "unrealistic" | "ready";
  dropV: number | null;
  dropPct: number | null;
  loadVoltageV: number | null;
  lossW: number | null;
  withinTarget: boolean | null;
};

/** A voltage-drop check only. It does not select a wire, fuse or ampacity. */
export function calculateVoltageDrop(input: VoltageDropInput): VoltageDropResult {
  const result: VoltageDropResult = { status: "unknown", missing: [], invalid: [], unsupported: [],
    dropV: null, dropPct: null, loadVoltageV: null, lossW: null, withinTarget: null };
  requireNumber(result, "Supply voltage (V)", input.voltageV, 1, 60);
  requireNumber(result, "Load current (A)", input.currentA, 0.01, 10000);
  requireNumber(result, "One-way length (m)", input.oneWayLengthM, 0.01, 1000);
  if (input.wireMm2 == null) result.missing.push("Cable size");
  else if (!VOLTAGE_DROP_WIRES.some(wire => wire.mm2 === input.wireMm2)) result.invalid.push("Choose a listed copper cable size.");
  if (input.targetPct == null) result.missing.push("Comparison target");
  else if (input.targetPct !== 3 && input.targetPct !== 10) result.invalid.push("Choose a 3% or 10% comparison target.");
  if (result.invalid.length) return { ...result, status: "invalid" };
  if (result.missing.length) return result;
  const dropV = voltageDrop(input.currentA!, input.oneWayLengthM!, input.wireMm2!);
  const dropPct = dropV / input.voltageV! * 100;
  return { ...result, status: dropV >= input.voltageV! ? "unrealistic" : "ready", dropV, dropPct,
    loadVoltageV: dropV >= input.voltageV! ? null : input.voltageV! - dropV,
    lossW: input.currentA! * dropV, withinTarget: dropPct <= input.targetPct! };
}
